import {
  getDefaultSelectedOutcome,
  isOutcomeTradable,
} from "./bondPredictDetailHelpers";
import { formatChancePct } from "./bondPredictHelpers";

export const ORDER_TYPES = {
  MARKET: "MARKET",
  LIMIT: "LIMIT",
};

export const ORDER_SIDES = {
  BUY: "BUY",
  SELL: "SELL",
};

export const MARKET_ORDER_SLIPPAGE = 0.02;

export const formatTradeCoin = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `Coin ${n.toFixed(2)}`;
};

export const formatTradePrice01 = (price) => {
  const n = Number(price);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(4);
};

export const formatTradeChancePct = formatChancePct;

export const formatTradeShares = (q) => {
  const n = Number(q);
  if (!Number.isFinite(n)) return "—";
  return n >= 100 ? n.toFixed(0) : n.toFixed(2);
};

export const getBestBidAsk = (orderBook) => {
  const bestBid =
    orderBook?.bestBid ??
    (Array.isArray(orderBook?.bids) && orderBook.bids.length ? orderBook.bids[0]?.price : null);
  const bestAsk =
    orderBook?.bestAsk ??
    (Array.isArray(orderBook?.asks) && orderBook.asks.length ? orderBook.asks[0]?.price : null);
  return { bestBid, bestAsk };
};

export const getDefaultLimitPrice = (outcome, orderBook, side) => {
  const { bestBid, bestAsk } = getBestBidAsk(orderBook);
  if (side === ORDER_SIDES.BUY && typeof bestAsk === "number") {
    return Math.min(1, bestAsk);
  }
  if (side === ORDER_SIDES.SELL && typeof bestBid === "number") {
    return Math.max(0, bestBid);
  }
  const fromOutcome =
    typeof outcome?.displayPrice === "number"
      ? outcome.displayPrice
      : typeof outcome?.ammState?.price === "number"
      ? outcome.ammState.price
      : null;
  if (typeof fromOutcome === "number") return Math.min(1, Math.max(0, fromOutcome));
  return null;
};

export const getExecutableLevel = (orderBook, side) => {
  const { bestBid, bestAsk } = getBestBidAsk(orderBook);
  if (side === ORDER_SIDES.BUY) {
    const topAsk =
      Array.isArray(orderBook?.asks) && orderBook.asks.length ? orderBook.asks[0] : null;
    return {
      label: "Best ask",
      price: topAsk?.price ?? bestAsk,
      quantity: topAsk?.quantity ?? null,
    };
  }
  const topBid =
    Array.isArray(orderBook?.bids) && orderBook.bids.length ? orderBook.bids[0] : null;
  return {
    label: "Best bid",
    price: topBid?.price ?? bestBid,
    quantity: topBid?.quantity ?? null,
  };
};

export const estimateOrderTotal = ({ orderType, side, shares, price, orderBook }) => {
  const qty = Number(shares) || 0;
  if (qty <= 0) return null;
  if (orderType === ORDER_TYPES.MARKET) {
    const { price: levelPrice } = getExecutableLevel(orderBook, side);
    if (typeof levelPrice !== "number") return null;
    return qty * levelPrice;
  }
  const price01 = Number(price);
  if (!Number.isFinite(price01)) return null;
  return qty * price01;
};

export const validateOrderForm = ({ orderType, side, shares, price, orderBook, userShares }) => {
  const qty = Number(shares);
  if (!Number.isFinite(qty) || qty <= 0) {
    return "Enter a valid number of shares.";
  }

  if (side === ORDER_SIDES.SELL && qty > Number(userShares || 0) + 1e-9) {
    return `You only hold ${formatTradeShares(userShares)} shares for this outcome.`;
  }

  if (orderType === ORDER_TYPES.LIMIT) {
    const price01 = Number(price);
    if (!Number.isFinite(price01) || price01 <= 0 || price01 > 1) {
      return "Enter a limit price between 0 and 1 (e.g. 0.61).";
    }
    return null;
  }

  const { bestBid, bestAsk } = getBestBidAsk(orderBook);
  if (side === ORDER_SIDES.BUY && typeof bestAsk !== "number") {
    return "No ask liquidity available for a market buy right now.";
  }
  if (side === ORDER_SIDES.SELL && typeof bestBid !== "number") {
    return "No bid liquidity available for a market sell right now.";
  }
  return null;
};

export const buildOrderPayload = ({
  marketId,
  outcomeId,
  side,
  orderType,
  shares,
  price,
  orderBook,
}) => {
  const payload = {
    marketId,
    outcomeId,
    side: String(side).toUpperCase(),
    type: orderType,
    quantity: Number(shares),
  };

  if (orderType === ORDER_TYPES.LIMIT) {
    payload.price = Number(price);
    return payload;
  }

  const { bestBid, bestAsk } = getBestBidAsk(orderBook);
  if (side === ORDER_SIDES.BUY) {
    payload.priceRange = { max: Math.min(1, bestAsk * (1 + MARKET_ORDER_SLIPPAGE)) };
  } else {
    payload.priceRange = { min: Math.max(0, bestBid * (1 - MARKET_ORDER_SLIPPAGE)) };
  }
  return payload;
};

export const getPlaceOrderToast = ({ side, orderType, fillResult, message }) => {
  const sideLabel = side === ORDER_SIDES.SELL ? "Sell" : "Buy";
  let tone = "success";
  let title = `${sideLabel} order placed`;

  if (orderType === ORDER_TYPES.MARKET) {
    if (fillResult === "none") {
      tone = "warn";
      title = side === ORDER_SIDES.BUY ? "No shares bought" : "No shares sold";
    } else if (fillResult === "partial") {
      tone = "info";
      title = "Partial fill";
    } else if (fillResult === "full") {
      title = side === ORDER_SIDES.BUY ? "Buy completed" : "Sell completed";
    }
  } else if (fillResult === "full") {
    title = "Order filled";
  } else if (fillResult === "partial") {
    tone = "info";
    title = "Partly filled";
  } else if (fillResult === "resting") {
    tone = "info";
    title = "Limit order open";
  }

  return {
    tone,
    text: message || title,
  };
};

export const findPositionShares = (positions, marketId, outcomeId) => {
  if (!Array.isArray(positions) || !outcomeId) return 0;
  const match = positions.find((p) => {
    const pMarketId = p?.market?._id || p?.market;
    const pOutcomeId = p?.outcome?._id || p?.outcome;
    return String(pMarketId) === String(marketId) && String(pOutcomeId) === String(outcomeId);
  });
  return Number(match?.shares || 0);
};

export const resolveMarketListTradeTarget = (market) => {
  if (!market?._id) return null;

  const defaultOutcome = getDefaultSelectedOutcome(market);
  if (defaultOutcome?._id && isOutcomeTradable(market, defaultOutcome)) {
    return { market, outcome: defaultOutcome };
  }

  const outcomes = Array.isArray(market.outcomes) ? market.outcomes : [];
  const tradableOutcome = outcomes.find(
    (outcome) => outcome?._id && isOutcomeTradable(market, outcome)
  );
  if (!tradableOutcome) return null;

  return { market, outcome: tradableOutcome };
};

export const resolvePortfolioTradeTarget = (mode, position, order) => {
  if (mode === "holdings" && position) {
    const market = position.market || {};
    const outcome = position.outcome || null;
    const outcomeId = outcome?._id || outcome;
    if (!market?._id && !position?.market) return null;
    return {
      market: typeof market === "object" ? market : { _id: position.market },
      outcome: typeof outcome === "object" ? outcome : outcomeId ? { _id: outcomeId } : null,
    };
  }
  if (mode === "orders" && order) {
    const market = order.market || {};
    const outcome = order.outcome || null;
    const outcomeId = outcome?._id || outcome;
    if (!market?._id && !order?.market) return null;
    return {
      market: typeof market === "object" ? market : { _id: order.market },
      outcome: typeof outcome === "object" ? outcome : outcomeId ? { _id: outcomeId } : null,
    };
  }
  return null;
};
